import { IconArrowRight, IconChevronLeft } from '@tabler/icons-react'
import { useRef } from 'react'
import { z } from 'zod'

import Button from 'components/ui/button'
import Input from 'components/ui/input'
import Text from 'components/ui/text'
import { ButtonCTA } from 'components/ui/button-cta'
import { ONBOARDING_MOTIVATION_OPTIONS } from 'features/onboarding/constants'
import Select from 'components/ui/select'
import { Form } from 'components/ui/form'
import { useOnboarding } from 'features/onboarding/hooks/use-onboarding'
import {
  ONBOARDING_STEPS,
  TRANSLATION_KEYS,
} from 'features/onboarding/config/steps'
import { translate } from 'utils/translation'

interface Step4Props {
  onNext: () => void
  onPrevious: () => void
}

const step4Schema = z
  .object({
    motivation: z.string({
      required_error: translate(TRANSLATION_KEYS.selectOption),
    }),
    specifiedMotivation: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.motivation === 'Other') {
        return !!data.specifiedMotivation
      }
      return true
    },
    {
      message: translate(TRANSLATION_KEYS.inputLabel),
      path: ['specifiedMotivation'],
    },
  )

type Step4FormValues = z.infer<typeof step4Schema>

export const Step4 = ({ onNext, onPrevious }: Step4Props) => {
  const onboardingState = useOnboarding()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const selectedMotivationRef = useRef<string>('')

  const autoFocusInput = (value: string) => {
    if (value === 'Other') {
      const frameId = requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
      return () => cancelAnimationFrame(frameId)
    }
  }

  const handleSubmit = (values: Step4FormValues) => {
    if (values) {
      onboardingState.updateFormData({
        step4: {
          motivation: values.motivation,
          specifiedMotivation:
            values.motivation === 'Other' ? values.specifiedMotivation : '',
        },
      })
      onNext()
    }
  }

  return (
    <section className="tw-mx-auto tw-flex tw-min-h-[calc(100vh-200px)] tw-w-full tw-max-w-[640px] tw-flex-col">
      <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-start tw-gap-8 tw-pt-8 tw-text-center">
        <Text
          as="h2"
          id="step-heading"
          className="tw-m-0 tw-text-2xl tw-font-bold md:tw-text-3xl"
          boldClassName="tw-text-orange-500"
        >
          {ONBOARDING_STEPS.step4.title}
        </Text>

        <Form
          onSubmit={handleSubmit}
          schema={step4Schema}
          className="tw-flex tw-w-full tw-max-w-[320px] tw-flex-col tw-items-center tw-justify-center tw-gap-6"
          defaultValues={onboardingState.state.formData.step4 ?? undefined}
        >
          {({ register, setValue, watch, formState }) => {
            const selectedMotivation = watch('motivation')
            const { ref, ...rest } = register('specifiedMotivation')
            selectedMotivationRef.current = selectedMotivation
            return (
              <>
                <Select
                  options={ONBOARDING_MOTIVATION_OPTIONS}
                  buttonClassName="!tw-py-2 !tw-h-10"
                  className="tw-w-full tw-max-w-[288px] md:tw-max-w-[320px]"
                  placeholder={translate(TRANSLATION_KEYS.selectOption)}
                  value={watch('motivation')}
                  onChange={({ value }) => {
                    setValue('motivation', value, { shouldValidate: true })
                    autoFocusInput(value ?? '')
                  }}
                />

                {selectedMotivation === 'Other' && (
                  <Input
                    required
                    placeholder={translate(TRANSLATION_KEYS.other)}
                    label={translate(TRANSLATION_KEYS.inputLabel)}
                    error={formState.errors.specifiedMotivation}
                    ref={(e) => {
                      ref(e)
                      inputRef.current = e
                    }}
                    {...rest}
                  />
                )}

                <ButtonCTA
                  type="submit"
                  size="xl"
                  className="tw-mt-2"
                  disabled={!watch('motivation')}
                >
                  {translate(TRANSLATION_KEYS.continueButton)}{' '}
                  <IconArrowRight size={20} aria-hidden="true" />
                </ButtonCTA>
              </>
            )
          }}
        </Form>

        <div className="tw-mt-auto tw-flex tw-justify-center tw-gap-3">
          <Button
            size="lg"
            variant="neutral-outlined"
            onClick={onPrevious}
            aria-label={translate(TRANSLATION_KEYS.backButton)}
          >
            <IconChevronLeft size={20} aria-hidden="true" />{' '}
            {translate(TRANSLATION_KEYS.backButton)}
          </Button>
        </div>
      </div>
    </section>
  )
}
